import { filter, find, get } from 'lodash';

import { EXPENSE_RATIO_INPUT_METHOD, EXPENSE_SUMMARIZED_ENTRY_MODE } from 'shared/constants/expenses';
import { expenseForecastFormHelpers } from 'components/underwriting/income-approach/expenses/expense-forecast/expenseForecastFormConfig';
import { calculateOperatingExpenses } from 'components/underwriting/income-approach/expenses/operating-expenses/expense-history/helpers';
import { getOtherVCLossPercentage } from 'components/underwriting/income-approach/helpers';
import {
  BASIS_TYPES,
  DEFAULT_EXPENSES_KEYS,
  DEFAULT_EXPENSES_TITLES,
  EXPENSE_PERIOD_KEYS,
} from 'shared/constants/expenses';
import { divide } from 'shared/utils/numberOperations';
import {
  CommercialReimbursement,
  CommercialRentRollUnit,
  OtherIncomeItem,
  ResidentialRentRollUnit,
} from 'types/underwriting/income-approach';
import {
  EntryMode,
  ExpenseForecast,
  ExpenseHistory,
  ExpenseItem,
  InputMethod,
} from 'types/underwriting/income-approach/expenses';
import { UnderwritingOutput } from 'types/underwriting/underwriting';

export const INCOME_APPROACH_BASIS_TYPES = {
  borrower: 'borrower',
  projected: 'projected',
};

export type IncomeApproachBasisType = (typeof INCOME_APPROACH_BASIS_TYPES)[keyof typeof INCOME_APPROACH_BASIS_TYPES];

export const incomeApproachCalculations = {
  calculateResidentialIncomeTotal: (
    underwritingData: UnderwritingOutput,
    type: IncomeApproachBasisType = INCOME_APPROACH_BASIS_TYPES.borrower
  ) => {
    const rentRoll: ResidentialRentRollUnit[] = get(underwritingData, 'incomeApproach.residential.rentRoll', []);
    const rentField = type === INCOME_APPROACH_BASIS_TYPES.borrower ? 'rent' : 'projectedRent';
    const total = rentRoll.reduce((sum: any, unit: ResidentialRentRollUnit) => sum + (unit[rentField] || 0), 0);
    return total * 12;
  },
  calculateCommercialIncomeTotal: (
    underwritingData: UnderwritingOutput,
    type: IncomeApproachBasisType = INCOME_APPROACH_BASIS_TYPES.borrower
  ) => {
    const rentRoll: CommercialRentRollUnit[] = get(underwritingData, 'incomeApproach.commercial.rentRoll', []);
    const rentField = type === INCOME_APPROACH_BASIS_TYPES.borrower ? 'rent' : 'projectedRent';
    const total = rentRoll.reduce((sum: any, unit: CommercialRentRollUnit) => sum + (unit[rentField] || 0), 0);
    return total * 12;
  },
  calculateOtherIncomeTotal: (underwritingData: UnderwritingOutput) => {
    const otherIncome: OtherIncomeItem[] = get(underwritingData, 'incomeApproach.otherIncome', []);
    const total = otherIncome.reduce((sum: number, income: OtherIncomeItem) => sum + (income.amount || 0), 0);
    return total;
  },
  calculateCommercialReimbursementGrossTotal: (underwritingData: UnderwritingOutput) => {
    const commercialReimbursements: CommercialReimbursement[] = get(
      underwritingData,
      'incomeApproach.commercial.reimbursements',
      []
    );
    const total = commercialReimbursements.reduce(
      (sum: number, commercialReimbursement: CommercialReimbursement) =>
        sum + (commercialReimbursement.grossAnnualValue || 0),
      0
    );
    return total;
  },
  calculatePotentialGrossIncome: (
    underwritingData: UnderwritingOutput,
    type: IncomeApproachBasisType = INCOME_APPROACH_BASIS_TYPES.borrower
  ) => {
    const residentialIncomeTotal = incomeApproachCalculations.calculateResidentialIncomeTotal(underwritingData, type);
    const commercialIncomeTotal = incomeApproachCalculations.calculateCommercialIncomeTotal(underwritingData, type);
    const otherIncomeTotal = incomeApproachCalculations.calculateOtherIncomeTotal(underwritingData);
    const commercialReimbursementsGrossTotal =
      incomeApproachCalculations.calculateCommercialReimbursementGrossTotal(underwritingData);
    const potentialGrossIncome =
      residentialIncomeTotal + commercialIncomeTotal + otherIncomeTotal + commercialReimbursementsGrossTotal;
    return potentialGrossIncome;
  },
  calculateResidentialVCLoss: (
    underwritingData: UnderwritingOutput,
    type: IncomeApproachBasisType = INCOME_APPROACH_BASIS_TYPES.borrower
  ) => {
    const residentialVCLossPercentage = get(underwritingData, 'incomeApproach.residential.vcLossPercentage', 0);
    const residentialIncomeTotal = incomeApproachCalculations.calculateResidentialIncomeTotal(underwritingData, type);
    const residentialVCLoss = divide(residentialVCLossPercentage, 100) * residentialIncomeTotal;
    return residentialVCLoss;
  },
  calculateCommercialVCLoss: (
    underwritingData: UnderwritingOutput,
    type: IncomeApproachBasisType = INCOME_APPROACH_BASIS_TYPES.borrower
  ) => {
    const commercialVCLossPercentage = get(underwritingData, 'incomeApproach.commercial.vcLossPercentage', 0);
    const commercialIncomeTotal = incomeApproachCalculations.calculateCommercialIncomeTotal(underwritingData, type);
    const commercialVCLoss = divide(commercialVCLossPercentage, 100) * commercialIncomeTotal;
    return commercialVCLoss;
  },
  calculateTotalOtherVCLoss: (underwritingData: UnderwritingOutput) => {
    const otherIncome: OtherIncomeItem[] = get(underwritingData, 'incomeApproach.otherIncome', []);
    const commercialVCLossPercentage = get(underwritingData, 'incomeApproach.commercial.vcLossPercentage', 0);
    const residentialVCLossPercentage = get(underwritingData, 'incomeApproach.residential.vcLossPercentage', 0);
    const totalOtherVCLoss = otherIncome.reduce((sum: any, item: OtherIncomeItem) => {
      const vcLossPercentage = getOtherVCLossPercentage(item, residentialVCLossPercentage, commercialVCLossPercentage);
      const amount = item.amount || 0;
      return sum + divide(vcLossPercentage, 100) * amount;
    }, 0);
    return totalOtherVCLoss;
  },
  calculateTotalCommercialReimbursementsVCLoss: (underwritingData: UnderwritingOutput) => {
    const commercialReimbursements: CommercialReimbursement[] = get(
      underwritingData,
      'incomeApproach.commercial.reimbursements',
      []
    );
    const totalCommercialReimbursementsVCLoss = commercialReimbursements.reduce(
      (sum: number, commercialReimbursement: CommercialReimbursement) =>
        sum + (commercialReimbursement.grossVCLoss || 0),
      0
    );
    return totalCommercialReimbursementsVCLoss;
  },
  calculateEffectiveGrossIncome: (
    underwritingData: UnderwritingOutput,
    type: IncomeApproachBasisType = INCOME_APPROACH_BASIS_TYPES.borrower
  ) => {
    const potentialGrossIncome = incomeApproachCalculations.calculatePotentialGrossIncome(underwritingData, type);
    const residentialVCLoss = incomeApproachCalculations.calculateResidentialVCLoss(underwritingData, type);
    const commercialVCLoss = incomeApproachCalculations.calculateCommercialVCLoss(underwritingData, type);
    const totalOtherVCLoss = incomeApproachCalculations.calculateTotalOtherVCLoss(underwritingData);
    const totalCommercialReimbursementsVCLoss =
      incomeApproachCalculations.calculateTotalCommercialReimbursementsVCLoss(underwritingData);

    const effectiveGrossIncome =
      potentialGrossIncome -
      residentialVCLoss -
      commercialVCLoss -
      totalOtherVCLoss -
      totalCommercialReimbursementsVCLoss;
    return effectiveGrossIncome;
  },
  calculateExpenseForecastTotal: (
    underwritingData: UnderwritingOutput,
    type: IncomeApproachBasisType = INCOME_APPROACH_BASIS_TYPES.borrower
  ) => {
    const expenseForecasts: ExpenseForecast[] = get(underwritingData, 'incomeApproach.expenses.expenseForecasts', []);
    const grossBuildingArea = get(underwritingData, 'property.grossBuildingArea', 0);
    const residentialUnits = get(underwritingData, 'property.residentialUnits', 0);
    const effectiveGrossIncome = incomeApproachCalculations.calculateEffectiveGrossIncome(underwritingData, type);

    const expenseForecastEntryMode: EntryMode | undefined = get(
      underwritingData,
      'incomeApproach.expenses.expenseForecastEntryMode'
    );

    if (expenseForecastEntryMode === EXPENSE_SUMMARIZED_ENTRY_MODE) {
      const forecastInputMethod = get(underwritingData, 'incomeApproach.expenses.expenseForecastInputMethod');
      const totalExpenseForecastAmount = get(underwritingData, 'incomeApproach.expenses.totalExpenseForecastAmount', 0);
      const expenseForecastRatio = get(underwritingData, 'incomeApproach.expenses.expenseForecastRatio', 0);

      let amount = 0;
      if (forecastInputMethod === EXPENSE_RATIO_INPUT_METHOD) {
        const effectiveGrossIncome = incomeApproachCalculations.calculateEffectiveGrossIncome(underwritingData, type);
        const expenses = calculateOperatingExpenses(
          effectiveGrossIncome,
          expenseForecastRatio,
          EXPENSE_RATIO_INPUT_METHOD
        );
        amount = expenses.amount;
      } else {
        amount = totalExpenseForecastAmount;
      }

      return amount;
    }

    const expenseHistoryEntryMode: EntryMode | undefined = get(
      underwritingData,
      'incomeApproach.expenses.expenseHistoryEntryMode'
    );
    const filteredExpenseForecasts =
      expenseHistoryEntryMode !== EXPENSE_SUMMARIZED_ENTRY_MODE
        ? expenseForecasts
        : filter(expenseForecasts, (expenseForecast: ExpenseForecast) =>
            Object.values(DEFAULT_EXPENSES_KEYS).includes(expenseForecast.key!)
          );

    const total = filteredExpenseForecasts.reduce((sum: number, expenseForecast: ExpenseForecast) => {
      const { concludedValue = 0, basis = BASIS_TYPES.psf } = expenseForecast;
      const values = expenseForecastFormHelpers.getConcludedValueByBasis(
        concludedValue,
        basis,
        grossBuildingArea,
        residentialUnits,
        effectiveGrossIncome
      );
      return sum + values.total;
    }, 0);
    return total;
  },
  calculateExpenseHistoryProFormaTotal: (underwritingData: UnderwritingOutput) => {
    const expenseHistoryEntryMode: EntryMode | undefined = get(
      underwritingData,
      'incomeApproach.expenses.expenseHistoryEntryMode'
    );

    const expenseHistoryInputMethod: InputMethod | undefined = get(
      underwritingData,
      'incomeApproach.expenses.expenseHistoryInputMethod'
    );
    const expenseHistory = get(underwritingData, 'incomeApproach.expenses.expenseHistory', []);
    const expenseHistoryProForma = find(expenseHistory, (expense: ExpenseHistory) => {
      return expense.period === EXPENSE_PERIOD_KEYS.PROJECTION;
    });

    if (!expenseHistoryProForma) {
      return 0;
    }

    if (expenseHistoryEntryMode !== EXPENSE_SUMMARIZED_ENTRY_MODE) {
      return (expenseHistoryProForma.expenses || []).reduce(
        (sum: number, expense) => sum + get(expense, 'value', 0),
        0
      );
    }

    if (expenseHistoryInputMethod !== EXPENSE_RATIO_INPUT_METHOD) {
      return expenseHistoryProForma.totalExpenseAmount || 0;
    }

    const effectiveGrossIncome = incomeApproachCalculations.calculateEffectiveGrossIncome(underwritingData);
    const { amount } = calculateOperatingExpenses(
      effectiveGrossIncome,
      expenseHistoryProForma.operatingExpenseRatio || 0,
      EXPENSE_RATIO_INPUT_METHOD
    );

    return amount;
  },
  calculateTotalOperatingExpenses: (
    underwritingData: UnderwritingOutput,
    type: IncomeApproachBasisType = INCOME_APPROACH_BASIS_TYPES.borrower
  ) => {
    const taxLiability = incomeApproachCalculations.calculateTaxLiability(underwritingData);
    const expenseForecastTotal = incomeApproachCalculations.calculateExpenseForecastTotal(underwritingData, type);
    const expenseHistoryProFormaTotal =
      incomeApproachCalculations.calculateExpenseHistoryProFormaTotal(underwritingData);
    const expenseTotal =
      type === INCOME_APPROACH_BASIS_TYPES.borrower
        ? expenseHistoryProFormaTotal || 0
        : expenseForecastTotal + taxLiability;

    console.log(type, { expenseForecastTotal, expenseHistoryProFormaTotal, expenseTotal });

    return expenseTotal;
  },
  calculateTaxLiability: (underwritingData: UnderwritingOutput) => {
    const projectedTaxLiability = get(underwritingData, 'incomeApproach.taxes.projected.taxLiability');
    const taxLiability = get(underwritingData, 'incomeApproach.taxes.taxLiability');
    return projectedTaxLiability || taxLiability;
  },
  calculateTotalOperatingExpensesExcludingRealEstateTaxes: (
    underwritingData: UnderwritingOutput,
    type: IncomeApproachBasisType = INCOME_APPROACH_BASIS_TYPES.borrower
  ) => {
    const taxLiability = incomeApproachCalculations.calculateTaxLiability(underwritingData);
    const totalOperatingExpenses = incomeApproachCalculations.calculateTotalOperatingExpenses(underwritingData, type);

    if (type === INCOME_APPROACH_BASIS_TYPES.borrower) {
      const expenseHistoryEntryMode: EntryMode | undefined = get(
        underwritingData,
        'incomeApproach.expenses.expenseHistoryEntryMode'
      );
      const expenseHistory = get(underwritingData, 'incomeApproach.expenses.expenseHistory', []);
      const expenseHistoryProForma = find(expenseHistory, (expense: ExpenseHistory) => {
        return expense.period === EXPENSE_PERIOD_KEYS.PROJECTION;
      });

      if (!expenseHistoryProForma) {
        return 0;
      }

      const expenseHistoryProFormaTaxes = expenseHistoryProForma.expenses?.find(
        (expense: ExpenseItem) => expense.category === DEFAULT_EXPENSES_TITLES.realEstateTaxes
      );
      if (expenseHistoryProFormaTaxes && expenseHistoryEntryMode !== EXPENSE_SUMMARIZED_ENTRY_MODE) {
        return totalOperatingExpenses - expenseHistoryProFormaTaxes.value || 0;
      }

      return totalOperatingExpenses;
    }
    return totalOperatingExpenses - taxLiability;
  },
  calculateOperatingExpenseRatio: (
    underwritingData: UnderwritingOutput,
    type: IncomeApproachBasisType = INCOME_APPROACH_BASIS_TYPES.borrower
  ) => {
    const totalOperatingExpenses = incomeApproachCalculations.calculateTotalOperatingExpenses(underwritingData, type);
    const effectiveGrossIncome = incomeApproachCalculations.calculateEffectiveGrossIncome(underwritingData, type);
    console.log(type, { totalOperatingExpenses, effectiveGrossIncome });
    return divide(totalOperatingExpenses, effectiveGrossIncome) * 100;
  },
  calculateNetOperatingIncome: (
    underwritingData: UnderwritingOutput,
    type: IncomeApproachBasisType = INCOME_APPROACH_BASIS_TYPES.borrower
  ) => {
    const effectiveGrossIncome = incomeApproachCalculations.calculateEffectiveGrossIncome(underwritingData, type);
    const totalOperatingExpenses = incomeApproachCalculations.calculateTotalOperatingExpenses(underwritingData, type);
    return effectiveGrossIncome - totalOperatingExpenses;
  },
};
